import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import Ticket from "../../../interfaces/ticket/Ticket";
import { Badge, Button, Dropdown } from "react-bootstrap";
import copylink from "../../../assets/images/copylink.svg";
import ClickToCopyDiv from "../../ui/ClickToCopyContainer/ClickToCopyDiv";
import { getStyledTagColors, getStatusColors } from "../../../utilities/colorUtils";
import "../taskPage/taskpage.css";
import DOMPurify from "dompurify";
import { getTicketById, getAssignableUsers, updateTicketAssignee, closeTicket, getTicketTags, addTagToTicket, removeTagFromTicket, updateTicketStoryPoints, updateTicketPriority } from "../../../services/ticketServices/TicketService";
import { addComment } from "../../../services/commentServices/CommentService";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import CommentCard from "./CommentCard";
import CommentInput from "./CommentInput";
import AttachmentItem from './AttachmentItem';
import { Attachment, Comment } from "../../../interfaces/ticket/Comment";
import Tag from "../../../interfaces/tag/Tag";
import { getStatuses, updateTicketStatus } from "../../../services/statusServices/StatusService";
import TicketStatus from "../../../interfaces/ticket/TicketStatus";
import { User } from "../../../interfaces/User";
import { getSortedStatuses } from "../../../utilities/statusUtils";
import CloseTicketModal from "./modal/CloseTicketModal";
import Select, { SelectInstance } from 'react-select';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesUp, faAngleUp, faEquals, faAngleDown, faAnglesDown } from '@fortawesome/free-solid-svg-icons';

// Custom Toggle for the Reporter Dropdown to remove the green button
const CustomUserToggle = React.forwardRef<HTMLDivElement, { children: React.ReactNode, onClick: (e: React.MouseEvent<HTMLDivElement>) => void }>(
    ({ children, onClick }, ref) => (
        <div
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="d-flex align-items-center gap-2 p-1 rounded cursor-pointer hover:bg-gray-100 transition-colors"
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {children}
        </div>
    )
);

const IssuePage: React.FC = () => {
    const { issueId } = useParams<{ issueId: string }>();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [statuses, setStatuses] = useState<TicketStatus[]>([]);
    const [assignableUsers, setAssignableUsers] = useState<User[]>([]);
    const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);
    const [showCloseModal, setShowCloseModal] = useState<boolean>(false);
    const [isClosingTicket, setIsClosingTicket] = useState<boolean>(false);
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const [isAddingTag, setIsAddingTag] = useState<boolean>(false);

    // New state for handling Assignee click-to-edit
    const [isEditingAssignee, setIsEditingAssignee] = useState<boolean>(false);

    const selectRef = useRef<SelectInstance<{ value: number; label: string; }>>(null);

    const getPriorityIcon = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'highest':
                return <><FontAwesomeIcon icon={faAnglesUp} className="text-red-500" /> Highest</>;
            case 'high':
                return <><FontAwesomeIcon icon={faAngleUp} className="text-orange-500" /> High</>;
            case 'medium':
                return <><FontAwesomeIcon icon={faEquals} className="text-yellow-500" /> Medium</>;
            case 'low':
                return <><FontAwesomeIcon icon={faAngleDown} className="text-green-500" /> Low</>;
            case 'lowest':
                return <><FontAwesomeIcon icon={faAnglesDown} className="text-green-500" /> Lowest</>;
            default:
                return null;
        }
    }

    useEffect(() => {
        if (isAddingTag) {
            selectRef.current?.focus();
        }
    }, [isAddingTag]);

    const sortedStatuses = getSortedStatuses();

    const fetchTicketData = async () => {
        if (currentUser && issueId) {
            setLoading(true);
            const token = await currentUser.getIdToken();
            const [ticketData, statusData, usersData, tagsData] = await Promise.all([
                getTicketById(issueId, token, true, true),
                getStatuses(token),
                getAssignableUsers(token),
                getTicketTags(token)
            ]);

            setTicket(ticketData);
            if (ticketData?.comments) setComments(ticketData.comments);
            if (statusData) setStatuses(statusData);
            if (usersData) setAssignableUsers(usersData);
            if (tagsData) setAllTags(tagsData);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTicketData();
    }, [currentUser, issueId]);

    const handleCommentSubmit = async () => {
        if (!currentUser || !issueId || (!newComment.trim() && attachments.length === 0) || isSubmittingComment) return;
        setIsSubmittingComment(true);
        const token = await currentUser.getIdToken();

        const payload = {
            authorEmployeeId: currentUser.uid,
            body: newComment,
            attachments: attachments.length > 0 ? attachments : undefined
        };

        const createdComment = await addComment(issueId, payload, token);
        if (createdComment) {
            fetchTicketData();
            setNewComment("");
            setAttachments([]);
        }
        setIsSubmittingComment(false);
    };

    const handleFilesChange = (files: File[]) => {
        setAttachments(files);
    };

    const handleStatusChange = async (statusId: string) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalStatus = ticket.status;
        // Optimistic UI update
        setTicket(prevTicket => prevTicket ? { ...prevTicket, status: statusId } : null);

        const token = await currentUser.getIdToken();
        const success = await updateTicketStatus(token, issueId, statusId);
        if (!success) {
            // Revert on failure
            setTicket(prevTicket => prevTicket ? { ...prevTicket, status: originalStatus } : null);
            // Optionally, show an error notification
        }
    };

    const handleAssigneeChange = async (assigneeId: string | null) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalAssignee = ticket.assignee;

        setIsEditingAssignee(false); // Exit edit mode

        // Optimistic UI update
        const newAssignee = assigneeId ? assignableUsers.find(user => user.id === assigneeId) : undefined;
        setTicket(prevTicket => prevTicket ? { ...prevTicket, assignee: newAssignee } : null);

        const token = await currentUser.getIdToken();
        const success = await updateTicketAssignee(token, issueId, assigneeId);
        if (!success) {
            // Revert on failure
            setTicket(prevTicket => prevTicket ? { ...prevTicket, assignee: originalAssignee } : null);
            // Optionally, show an error notification
        }
    };

    const handleStoryPointsChange = async (storyPoints: number | null) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalStoryPoints = ticket.storyPoints;

        // Optimistic UI update
        setTicket(prevTicket => prevTicket ? { ...prevTicket, storyPoints: storyPoints } : null);

        const token = await currentUser.getIdToken();
        const updatedTicket = await updateTicketStoryPoints(token, issueId, storyPoints);
        if (!updatedTicket) {
            // Revert on failure
            setTicket(prevTicket => prevTicket ? { ...prevTicket, storyPoints: originalStoryPoints } : null);
            // Optionally, show an error notification
        }
    };

    const handlePriorityChange = async (priority: string) => {
        if (!currentUser || !issueId || !ticket) return;
        const originalPriority = ticket.priority;

        // Optimistic UI update
        setTicket(prevTicket => prevTicket ? { ...prevTicket, priority: priority as "Highest" | "High" | "Medium" | "Low" | "Lowest" } : null);

        const token = await currentUser.getIdToken();
        const updatedTicket = await updateTicketPriority(token, issueId, priority);
        if (!updatedTicket) {
            // Revert on failure
            setTicket(prevTicket => prevTicket ? { ...prevTicket, priority: originalPriority } : null);
            // Optionally, show an error notification
        }
    };

    const handleCloseTicket = async (comment: string) => {
        if (!currentUser || !issueId) return;
        setIsClosingTicket(true);
        const token = await currentUser.getIdToken();
        const success = await closeTicket(token, issueId, comment);
        if (success) {
            setShowCloseModal(false);
            fetchTicketData(); // Refresh data
        }
        setIsClosingTicket(false);
    };

    const handleAddTag = async (tagId: string) => {
        if (!currentUser || !issueId || !ticket) return;

        // Optimistic UI update
        const tagToAdd = allTags.find(tag => tag.id.toString() === tagId);
        if (tagToAdd) {
            setTicket(prevTicket => prevTicket ? { ...prevTicket, tags: [...prevTicket.tags, tagToAdd] } : null);
        }

        const token = await currentUser.getIdToken();
        const success = await addTagToTicket(token, issueId, tagId);
        if (!success) {
            // Revert on failure
            setTicket(prevTicket => prevTicket ? { ...prevTicket, tags: prevTicket.tags.filter(tag => tag.id.toString() !== tagId) } : null);
            // Optionally, show an error notification
        }
    };

    const handleRemoveTag = async (tagId: string) => {
        if (!currentUser || !issueId || !ticket) return;

        // Optimistic UI update
        setTicket(prevTicket => prevTicket ? { ...prevTicket, tags: prevTicket.tags.filter(tag => tag.id.toString() !== tagId) } : null);

        const token = await currentUser.getIdToken();
        const success = await removeTagFromTicket(token, issueId, tagId);
        if (!success) {
            // Revert on failure, need to find the removed tag from original state or allTags
            const tagToRevert = allTags.find(tag => tag.id.toString() === tagId);
            if (tagToRevert) {
                setTicket(prevTicket => prevTicket ? { ...prevTicket, tags: [...prevTicket.tags, tagToRevert] } : null);
            }
            // Optionally, show an error notification
        }
    };

    const RedirectToProfilePage = (userId: string) => {
        if (!userId) return;
        navigate(`/profile/user/${userId}`);
    };

    // Helper for Select Options
    const userOptions = [
        { value: null, label: 'Unassigned', user: null },
        ...assignableUsers.map(user => ({ value: user.id, label: user.displayName, user: user }))
    ];

    // Helper to render user options with Avatar in React-Select
    const formatOptionLabel = ({ label, user }: any) => (
        <div style={{ display: "flex", alignItems: "center" }}>
            {user ? <div style={{ width: '24px', height: '24px', marginRight: '8px' }}><AvatarImage size="xxs" userId={user.id} /></div> : null}
            <div>{label}</div>
        </div>
    );

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!ticket) {
        return <div>Ticket not found</div>;
    }

    const assignee = ticket.assignee;
    const reporter = ticket.reporter;
    const allAttachments = ticket.comments?.flatMap((c: Comment) => c.attachments) || [];
    const availableTags = allTags.filter(tag => !ticket.tags.some(t => t.id === tag.id));

    return (
        <>
            <div className="top top_bar_flx remove-input-txt-border mb-3">
                <div className="top_bar_tittle flex gap-2">
                    <h2 className="mb-0">Issue</h2> &#x2022;
                    <div className="my_badge">
                        <Dropdown>
                            <Dropdown.Toggle
                                className="status-dropdown-toggle"
                                style={getStatusColors(ticket.status)}
                            >
                                {ticket.status || "-"}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {sortedStatuses.map(status => {
                                    const statusColors = getStatusColors(status.name);
                                    return (
                                        <Dropdown.Item key={status.name} onClick={() => handleStatusChange(status.name)}>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    backgroundColor: statusColors.backgroundColor,
                                                    marginRight: '8px',
                                                }}
                                            ></span>
                                            {status.name}
                                        </Dropdown.Item>
                                    );
                                })}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                <div className="top_bar_right">
                    <Button variant="danger" className="btn_sm" onClick={() => setShowCloseModal(true)}>Close Ticket</Button>
                </div>
            </div>
            <div className="common_card mby_30 h-auto">
                <div className="task_given_flx">
                    <div className="task_given_left">
                        <div className="task_name_top">
                            <h3>{ticket.title}</h3>
                            <ClickToCopyDiv
                                textToCopy={`${process.env.REACT_APP_CURRENT_URL ?? window.location.origin}/issue/${ticket.publicId}`}
                                className="p-1 transition-colors duration-200 mt-2 flex"
                            >
                                <div className="link_copy flex gap-2 align-middle items-center hover:bg-gray-100 rounded-lg p-1">
                                    <span>
                                        <img src={copylink} alt="copylink" />
                                    </span>
                                    <p className="m-0">
                                        Issue ID: {ticket.publicId}
                                    </p>
                                </div>
                            </ClickToCopyDiv>
                        </div>
                    </div>
                </div>
                <div className="task_desc">
                    {/* Class 'stats' removed. Added a margin-bottom class like 'mb-3' if needed */}
                    <div className="description-container" style={{ flexDirection: 'column', display: 'flex' }}>
                        <h3>Description</h3>
                        <div
                            className="description-body"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(ticket.description || "", {
                                    ADD_ATTR: ['data-id', 'data-value', 'data-denotation-char', 'data-index', 'class'],
                                    ADD_TAGS: ['span'],
                                })
                            }}
                        />
                    </div>
                </div>
                {/* STATUS GRID START */}
                <div className="task_given_btm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4 mt-6">

                    {/* 1. Creation Date */}
                    <div className="stats">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Creation Date</h3>
                        <p className="font-medium text-gray-900 m-0">
                            {new Date(ticket.startDate).toLocaleDateString()}
                        </p>
                    </div>

                    {/* 2. Due Date */}
                    <div className="stats">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Due Date</h3>
                        <p className={`font-medium m-0 ${ticket.endDate ? 'text-gray-900' : 'text-gray-400'}`}>
                            {ticket.endDate ? new Date(ticket.endDate).toLocaleDateString() : '--'}
                        </p>
                    </div>

                    {/* 3. Sprint */}
                    <div className="stats">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Sprint</h3>
                        <p className="font-medium text-gray-900 m-0">
                            {ticket.sprint ? ticket.sprint.name : 'Backlog'}
                        </p>
                    </div>

                    {/* 4. Story Points */}
                    <div className="stats">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Story Points</h3>
                        <Dropdown className="my_dropdown">
                            <Dropdown.Toggle variant="light" id="dropdown-basic" className="w-full text-left d-flex justify-content-between align-items-center border-gray-300">
                                {ticket.storyPoints ?? 'None'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => handleStoryPointsChange(null)}>None</Dropdown.Item>
                                {[0, 1, 2, 3, 5, 8, 13, 21, 34, 40].map(points => (
                                    <Dropdown.Item key={points} onClick={() => handleStoryPointsChange(points)}>
                                        {points}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    {/* 5. Priority */}
                    <div className="stats">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Priority</h3>
                        <Dropdown className="my_dropdown">
                            <Dropdown.Toggle variant="light" id="dropdown-basic" className="w-full text-left d-flex justify-content-between align-items-center border-gray-300">
                                {getPriorityIcon(ticket.priority)}
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                                {["Highest", "High", "Medium", "Low", "Lowest"].map(priority => (
                                    <Dropdown.Item key={priority} onClick={() => handlePriorityChange(priority)}>
                                        {getPriorityIcon(priority)}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    {/* 6. Assignee */}
                    <div className="stats assignee_stats relative">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Assignee</h3>
                        {isEditingAssignee ? (
                            <div className="w-full" style={{ minWidth: '200px' }}>
                                <Select
                                    autoFocus
                                    defaultMenuIsOpen
                                    options={userOptions}
                                    formatOptionLabel={formatOptionLabel}
                                    onChange={(option: any) => handleAssigneeChange(option?.value || null)}
                                    onBlur={() => setIsEditingAssignee(false)}
                                    defaultValue={assignee ? { value: assignee.id, label: assignee.displayName, user: assignee } : { value: null, label: 'Unassigned', user: null }}
                                    placeholder="Assign..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        ) : (
                            <div
                                onClick={() => setIsEditingAssignee(true)}
                                className="d-flex align-items-center gap-2 p-2 rounded border border-transparent hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer"
                            >
                                <div style={{ width: '24px', height: '24px' }}>
                                    <AvatarImage size="xxs" userId={assignee?.id || ""} />
                                </div>
                                <span className="text-gray-900 font-medium truncate">
                                    {assignee ? assignee.displayName : 'Unassigned'}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* 7. Reporter */}
                    {/* 7. Reporter Section */}
                    {reporter && (
                        <div className="stats assignee_stats">
                            <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Reporter</h3>
                            <Dropdown className="my_dropdown w-full">
                                <Dropdown.Toggle as={CustomUserToggle} id="dropdown-reporter">
                                    {/* <div style={{ width: '24px', height: '24px' }}> */}
                                        <AvatarImage size="xxs" userId={reporter.id} />
                                    {/* </div> */}
                                    <span className="text-gray-900 font-medium truncate">
                                        {reporter.firstName + " " + reporter.lastName}
                                    </span>
                                </Dropdown.Toggle>

                                {/* STYLED USER CARD DROPDOWN */}
                                <Dropdown.Menu
                                    align="end"
                                    className="p-0 border-0 shadow-2xl rounded-xl overflow-hidden mt-1"
                                    style={{ zIndex: 1000 }}
                                >
                                    <div className="w-80 bg-white p-0">
                                        {/* Header / Background Decoration */}
                                        <div className="h-16 bg-gradient-to-r from-purple-100 to-blue-50 w-full"></div>

                                        <div className="px-5 pb-5 relative flex flex-col items-center"> {/* Added flex and items-center */}

                                            {/* Avatar - Centered and overlapping */}
                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 border-4 border-white rounded-full bg-white shadow-sm">
                                                <AvatarImage size="md" userId={reporter.id} />
                                            </div>

                                            {/* User Details - Centered text */}
                                            <div className="mt-16 mb-4 text-center"> {/* Increased margin-top and added text-center */}
                                                <h3 className="text-xl font-bold text-gray-900 m-0 leading-tight">
                                                    {reporter.firstName + " " + reporter.lastName}
                                                </h3>
                                                <p className="text-gray-500 text-sm m-0 break-all">
                                                    {reporter.email}
                                                </p>
                                            </div>

                                            <div className="h-px bg-gray-100 w-full mb-4"></div>

                                            {/* Actions */}
                                            <div className="grid grid-cols-2 gap-3 w-full"> {/* Added w-full to ensure grid stretches */}
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="w-full font-medium text-gray-700 hover:bg-gray-200 border-gray-200"
                                                >
                                                    Message
                                                </Button>
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="w-full font-medium"
                                                    onClick={() => RedirectToProfilePage(reporter.id || "")}
                                                >
                                                    Profile
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    )}

                    {/* 8. Tags (Spanning 2 columns for better fit) */}
                    <div className="stats tag-stats-container col-span-1 md:col-span-2 lg:col-span-1">
                        <h3 className="text-gray-500 text-sm font-semibold mb-1 uppercase tracking-wider">Tags</h3>
                        <div className="d-flex flex-wrap gap-2 items-center">
                            {ticket.tags.map((tag: Tag) => {
                                const { tagColor, textColor } = getStyledTagColors(tag.color, '#7e57c2');
                                return (
                                    <Badge
                                        key={tag.id}
                                        bg=""
                                        className="d-flex align-items-center px-2 py-1"
                                        style={{ backgroundColor: tagColor, color: textColor, fontSize: '0.75rem' }}
                                    >
                                        {tag.name.toUpperCase()}
                                        <button
                                            onClick={() => handleRemoveTag(tag.id.toString())}
                                            className="ms-2 bg-transparent border-0 p-0 text-current opacity-70 hover:opacity-100"
                                            style={{ lineHeight: 1 }}
                                        >
                                            ×
                                        </button>
                                    </Badge>
                                );
                            })}

                            {/* Add Tag Button / Select */}
                            <div className="min-w-[120px]">
                                {isAddingTag ? (
                                    <Select
                                        ref={selectRef}
                                        options={availableTags.map(tag => ({ value: tag.id, label: tag.name }))}
                                        onChange={(option: any) => {
                                            if (option) handleAddTag(option.value.toString());
                                            setIsAddingTag(false);
                                        }}
                                        onBlur={() => setIsAddingTag(false)}
                                        placeholder="Add..."
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                                            control: base => ({ ...base, minHeight: '30px', height: '30px', fontSize: '0.8rem' })
                                        }}
                                    />
                                ) : (
                                    <button
                                        className="text-gray-500 hover:text-primary hover:bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center transition-colors text-lg pb-1"
                                        onClick={() => setIsAddingTag(true)}
                                        title="Add Tag"
                                    >
                                        +
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* STATUS GRID END */}
            </div>
            <div className="common_card h-auto my-3">
                <div className="top border_tittle">
                    <div className="top_bar_tittle">
                        <h2>Attachments</h2>
                    </div>
                </div>
                <div className="p-4">
                    {allAttachments.length > 0 ? (
                        <div className="attachments-grid">
                            {allAttachments.map((attachment: Attachment) => (
                                <AttachmentItem key={attachment.id} attachment={attachment} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted">No attachments yet.</p>
                    )}
                </div>
            </div>
            <div className="common_card h-auto">
                <div className="top border_tittle">
                    <div className="top_bar_tittle">
                        <h2>Activity</h2>
                    </div>
                </div>
                <div className="p-4">
                    <div className="comment-list">
                        {comments.map((comment: Comment) => (
                            <CommentCard key={comment.id} comment={comment} />
                        ))}
                    </div>
                    <CommentInput
                        value={newComment}
                        onChange={setNewComment}
                        onFilesChange={handleFilesChange}
                        onSubmit={handleCommentSubmit}
                        attachments={attachments}
                        isSubmitting={isSubmittingComment}
                        users={assignableUsers}
                    />
                </div>
            </div>
            <CloseTicketModal
                show={showCloseModal}
                onHide={() => setShowCloseModal(false)}
                onSubmit={handleCloseTicket}
                isSubmitting={isClosingTicket}
            />
        </>
    );
};

export default IssuePage;